// 자원 분석 — 노드별 묶음과 그림 부품 (2026-10-02)
//
// 노드별로 보이라는 요청(SGA·PGA·UNDO·TEMP·세그먼트)을 섹션마다 따로 짜면 노드 머리말·순서·키가 섹션마다
// 갈린다. 묶는 규칙은 `groupByInst` 하나다(instId 가 없으면 1 — 단일 인스턴스·옛 응답).
// 노드가 하나면 머리말을 그리지 않는다(누를 것도 구분할 것도 없는 머리말은 소음이다).
import React from 'react';
import { ResponsiveContainer, Treemap } from 'recharts';
import { memoryModeOf } from '../../utils/memoryMode';

/** [{instId, ...}] → [{ instId, rows }] 노드 번호 순 */
export function groupByInst(rows) {
    const m = new Map();
    for (const r of rows || []) {
        const k = r.instId ?? 1;
        if (!m.has(k)) m.set(k, []);
        m.get(k).push(r);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([instId, list]) => ({ instId, rows: list }));
}

export function NodeHead({ instId, multi, t, children }) {
    if (!multi) return children || null;
    return (
        <div className="resana-node-head">
            <span className="resana-node-badge">{t('monitoring:res.node', { n: instId })}</span>
            {children}
        </div>
    );
}

/**
 * 테이블스페이스 — **그림과 표를 한 격자에**(요청). 예전에는 왼쪽 그래프·오른쪽 표가 따로라 줄이 맞지
 * 않았다. 막대는 지금 고른 정렬 기준의 값이다(사용률이면 %, 양이면 그 양의 최댓값 대비).
 */
export function UsageBarGrid({ rows, metric, labels, fmtMb, fmtPct, emptyText, selectedKey, onRowClick, rowHint }) {
    const isPct = metric === 'usedPct';
    const max = isPct ? 100 : Math.max(1, ...rows.map(r => Number(r[metric]) || 0));
    // 막대 칸 머리는 「그래프」 가 아니라 막대가 그리는 값의 이름(2026-10-03 요청) — 정렬 기준을 바꾸면 따라 바뀐다
    const barLabel = { usedPct: labels.pct, totalMb: labels.total, usedMb: labels.used, freeMb: labels.free }[metric] || labels.pct;
    return (
        <div className="resana-ugrid" role="table">
            <div className="resana-ugrid-row resana-ugrid-head" role="row">
                <span>{labels.name}</span><span title={labels.barHint}>{barLabel}</span>
                <span className="num">{labels.total}</span><span className="num">{labels.used}</span>
                <span className="num">{labels.free}</span><span className="num">{labels.pct}</span>
            </div>
            {rows.map(r => {
                const v = Number(r[metric]) || 0;
                const w = Math.max(0, Math.min(100, (v / max) * 100));
                const level = r.usedPct >= 85 ? 'danger' : (r.usedPct >= 70 ? 'warn' : 'ok');
                return (
                    <div
                        key={r.key || r.tablespaceName}
                        className={`resana-ugrid-row ${onRowClick ? 'clickable' : ''} ${selectedKey && selectedKey === r.tablespaceName ? 'selected' : ''}`}
                        role="row"
                        aria-selected={onRowClick ? selectedKey === r.tablespaceName : undefined}
                        tabIndex={onRowClick ? 0 : undefined}
                        title={onRowClick ? rowHint : undefined}
                        onClick={onRowClick ? () => onRowClick(r) : undefined}
                        onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(r); } } : undefined}
                    >
                        <span className="resana-mono resana-ugrid-name" title={r.tablespaceName}>{r.label || r.tablespaceName}</span>
                        <span className="resana-ugrid-barcell">
                            <span className={`resana-ugrid-bar ${isPct ? level : 'amount'}`} style={{ width: `${w}%` }} />
                            <span className="resana-ugrid-barval">{isPct ? fmtPct(r.usedPct) : fmtMb(v)}</span>
                        </span>
                        <span className="num">{fmtMb(r.totalMb)}</span>
                        <span className="num">{fmtMb(r.usedMb)}</span>
                        <span className="num">{fmtMb(r.freeMb)}</span>
                        <span className={`num ${level === 'danger' ? 'resana-pct-danger' : level === 'warn' ? 'resana-pct-warn' : ''}`}>{fmtPct(r.usedPct)}</span>
                    </div>
                );
            })}
            {rows.length === 0 && <div className="resana-empty-note">{emptyText}</div>}
        </div>
    );
}

// SGA 구성 요소 색 — 이름은 오라클 값 그대로(데이터 키)
const SGA_COLORS = {
    buffer_cache: '#3b82f6', 'shared pool': '#8b5cf6', 'large pool': '#14b8a6', 'java pool': '#f59e0b',
    'streams pool': '#ec4899', log_buffer: '#64748b', shared_io_pool: '#06b6d4', fixed_sga: '#94a3b8',
};
const sgaColor = (name) => SGA_COLORS[name] || '#a3a3a3';

const fmtSize = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(2)}GB` : `${Math.round(mb)}MB`);
const sizeLine = (c, freeLabel) => `${fmtSize(c.size)} · ${c.pct}%${c.freeMb > 0 ? ` (${freeLabel} ${fmtSize(c.freeMb)})` : ''}`;
// 글자 폭 어림(글자 크기 대비) — 구성 요소 이름은 영문 소문자·밑줄이라 이 정도면 넘치지 않는다
const CHAR_W = { bold: 0.62, normal: 0.56 };
const MIN_FONT = 9;
const MAX_FONT = 22;
/** 이 글자 크기로 줄이 폭에 들어가지 않으면 들어가는 크기로 줄인다. 최소보다 작아지면 null(안 그린다) */
const fitFont = (text, want, room, w) => {
    const f = Math.min(want, room / (String(text).length * w));
    return f >= MIN_FONT ? f : null;
};

/**
 * 상자 안 글자 크기는 **상자 크기에 비례**한다(2026-10-03 요청) — 큰 풀은 크게, 작은 풀은 작게 읽혀
 * 상자 크기의 뜻(몫)이 글자로도 드러난다. 기준은 넓이의 제곱근이고, 그 크기로 폭에 안 들어가면 들어가는
 * 크기까지 줄인다. 그래도 최소(9px)보다 작아야 하면 안 그린다 — 잘린 글자는 무엇인지 알 수 없다(2026-10-03 지적).
 * 글자는 상자 가운데(가로·세로)에 둔다.
 * 이름·값 전부는 상자 아래 범례에 있다.
 */
function TreemapCell(props) {
    const { x, y, width, height, name, value, pct, freeMb, freeLabel } = props;
    if (width <= 0 || height <= 0) return null;
    const pad = Math.max(4, Math.min(10, Math.sqrt(width * height) / 20));
    const room = width - pad * 2;
    const want = Math.max(MIN_FONT, Math.min(MAX_FONT, Math.sqrt(width * height) / 7));
    const full = sizeLine({ size: value, pct, freeMb }, freeLabel);
    const short = `${fmtSize(value)} · ${pct}%`;

    const nameFont = fitFont(name, want, room, CHAR_W.bold);
    const nameFits = nameFont && height >= nameFont + pad * 2;
    let val = null;
    let valFont = null;
    if (nameFits) {
        const wantVal = Math.max(MIN_FONT, nameFont * 0.82);
        const left = height - pad * 2 - nameFont * 1.25;   // 이름 줄과 줄 간격을 뺀 남은 높이
        for (const text of [full, short]) {
            const f = fitFont(text, wantVal, room, CHAR_W.normal);
            if (f && left >= f) { val = text; valFont = f; break; }
        }
    }
    // 글자 덩어리(이름 + 값)를 상자 한가운데에 둔다(2026-10-03 요청)
    const gap = nameFont ? nameFont * 0.25 : 0;
    const blockH = nameFits ? nameFont + (val ? gap + valFont : 0) : 0;
    const top = y + (height - blockH) / 2;
    const cx = x + width / 2;
    return (
        <g>
            <rect x={x} y={y} width={width} height={height} fill={sgaColor(name)} stroke="#fff" strokeWidth={2} rx={3}>
                <title>{`${name} — ${full}`}</title>
            </rect>
            {nameFits && (
                <text x={cx} y={top + nameFont * 0.85} textAnchor="middle" fill="#fff" fontSize={nameFont} fontWeight={600} pointerEvents="none">{name}</text>
            )}
            {val && (
                <text x={cx} y={top + nameFont + gap + valFont * 0.85} textAnchor="middle" fill="#fff" fontSize={valFont} pointerEvents="none">{val}</text>
            )}
        </g>
    );
}

/**
 * SGA 트리맵 — 상자 크기 = SGA 전체 중 그 구성 요소의 몫(요청). 노드마다 하나.
 * 비율은 그 노드의 SGA 합계 기준이다(남은 것만으로 100% 를 다시 만들지 않는다).
 */
export function SgaTreemap({ components, height = 220, freeLabel = '' }) {
    const total = components.reduce((s, c) => s + (Number(c.mb) || 0), 0) || 1;
    const data = components
        .filter(c => (Number(c.mb) || 0) > 0)
        .map(c => ({ name: c.component, size: Number(c.mb), freeMb: Number(c.freeMb) || 0, freeLabel, pct: Math.round((Number(c.mb) / total) * 1000) / 10 }));
    return (
        <>
            <div className="resana-treemap" style={{ height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <Treemap data={data} dataKey="size" nameKey="name" isAnimationActive={false} content={<TreemapCell />} />
                </ResponsiveContainer>
            </div>
            {/* 상자에 다 못 쓰는 것이 있으니 전부를 아래에 — 색으로 상자와 잇는다(큰 것부터) */}
            <div className="resana-treemap-legend">
                {[...data].sort((a, b) => b.size - a.size).map(c => (
                    <span key={c.name} className="resana-treemap-legend-item">
                        <i style={{ background: sgaColor(c.name) }} />
                        <b>{c.name}</b> {sizeLine(c, freeLabel)}
                    </span>
                ))}
            </div>
        </>
    );
}

/** UNDO 블록 구성 — 활성·미만료·만료를 한 막대에(그림으로 — 요청) */
export function UndoBlocksBar({ latest, labels }) {
    const a = Number(latest?.activeblks) || 0;
    const u = Number(latest?.unexpiredblks) || 0;
    const e = Number(latest?.expiredblks) || 0;
    const sum = a + u + e;
    if (!latest || sum === 0) return <div className="resana-empty-note">{labels.none}</div>;
    const seg = (v) => `${(v / sum) * 100}%`;
    return (
        <div className="resana-undo-bar-wrap">
            <div className="resana-undo-bar">
                <span className="active" style={{ width: seg(a) }} title={`${labels.active} ${a.toLocaleString()}`} />
                <span className="unexpired" style={{ width: seg(u) }} title={`${labels.unexpired} ${u.toLocaleString()}`} />
                <span className="expired" style={{ width: seg(e) }} title={`${labels.expired} ${e.toLocaleString()}`} />
            </div>
            <div className="resana-undo-legend">
                <span><i className="active" />{labels.active} {a.toLocaleString()}</span>
                <span><i className="unexpired" />{labels.unexpired} {u.toLocaleString()}</span>
                <span><i className="expired" />{labels.expired} {e.toLocaleString()}</span>
                <span className="resana-undo-ret">{labels.retention} {latest.tunedUndoretentionSec ?? '-'}s</span>
            </div>
        </div>
    );
}

/**
 * SGA·PGA 관리 방식(자동/수동)과 그 값이 무슨 뜻인지 — 노드 하나(2026-10-03 요청, 도움말과 별개로 늘 보인다).
 * 판정은 utils/memoryMode 한 곳. 「PGA 목표 0」 처럼 값만으로는 뜻을 알 수 없는 것을 문장으로 말한다.
 */
export function MemoryModeNote({ kind, params, t, fmtMb }) {
    const m = memoryModeOf(params);
    if (!m) return <div className="resana-memmode resana-memmode-unknown">{t('monitoring:res.memMode.unknown')}</div>;
    const part = m[kind];
    const v = m.values;
    const sizes = {
        memoryTarget: fmtMb(v.memoryTarget), sgaTarget: fmtMb(v.sgaTarget), sgaMaxSize: fmtMb(v.sgaMaxSize),
        pgaTarget: fmtMb(v.pgaTarget), pgaLimit: v.pgaLimit != null ? fmtMb(v.pgaLimit) : '',
    };
    const auto = part.mode !== 'manual';
    return (
        <div className={`resana-memmode ${auto ? 'auto' : 'manual'}`}>
            <div className="resana-memmode-head">
                <span className="resana-memmode-label">{t('monitoring:res.memMode.label')}</span>
                <span className={`resana-memmode-badge ${auto ? 'auto' : 'manual'}`}>{t(`monitoring:res.memMode.${kind}.${part.mode}.title`)}</span>
            </div>
            <ul className="resana-memmode-lines">
                <li>{t(`monitoring:res.memMode.${kind}.${part.mode}.desc`, sizes)}</li>
                {part.notes.map(n => <li key={n}>{t(`monitoring:res.memMode.${kind}.note.${n}`, sizes)}</li>)}
            </ul>
        </div>
    );
}
